import { Component, OnDestroy, OnInit } from "@angular/core";
import { Title } from "@angular/platform-browser";
import { MatDialog } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { Subject, takeUntil } from "rxjs";

import { GlobalService } from "src/app/_core/providers/global.service";
import { NotificationService } from "src/app/_core/providers/notification.service";
import { DialogService } from "src/app/_core/services/dialog.service";
import { BreadcrumbService } from "src/app/_core/services/breadcrumb.service";
import { getHomeMenuWithVisited } from "src/app/_core/constants/menu.constants";
import { ContactService } from "src/app/modules/contacts/services/contact.service";
import { PuestoDto } from "src/app/modules/contacts/interfaces/contact.interface";
import {
	PositionDialogComponent,
	PositionDialogData,
} from "./position-dialog.component";

/**
 * Admin de FUNCIONES Y ROLES de persona (ADR-0005). Un ROL (entidad Puesto) es lo que una
 * persona desempeña en un contrato/centro/cebe; puede ser GENERAL (disponible para todas las
 * funciones agregadas) o PARTICULAR de una FUNCIÓN (la FuncionAgregada del empleado, maestro
 * de Workday: solo lectura, aquí nunca se modifica).
 *
 * UX (decisión Pablo 2026-06-12): dos TABS independientes (generales / por función), cada uno
 * con su banner explicativo y su flujo; alta/edición en DIÁLOGO (espejo del diálogo de Persona),
 * nunca en un editor inline al pie de la página.
 */
@Component({
	selector: "app-positions",
	templateUrl: "./positions.page.html",
	styleUrls: ["./positions.page.scss"],
	standalone: false,
})
export class PositionsPageComponent implements OnInit, OnDestroy {
	private readonly menu = getHomeMenuWithVisited("/admin");
	private readonly destroy$ = new Subject<void>();

	puestos: PuestoDto[] = [];
	funciones: string[] = [];
	selectedFuncion: string | null = null;
	isLoading = false;

	/** Tab activo: 0 = roles generales, 1 = roles por función. */
	activeTab = 0;

	/** Etiqueta de opción para app-ds-select (las funciones son strings planos). */
	funcionLabelOf = (f: string | null): string => f ?? "";

	constructor(
		private readonly titleService: Title,
		private readonly translateService: TranslateService,
		private readonly globalService: GlobalService,
		private readonly breadcrumbService: BreadcrumbService,
		private readonly contactService: ContactService,
		private readonly notificationService: NotificationService,
		private readonly dialogService: DialogService,
		private readonly dialog: MatDialog,
	) {}

	ngOnInit(): void {
		this.globalService.setHeaderOptions([], null, [], this.menu);
		this.titleService.setTitle(
			this.translateService.instant("positions.title"),
		);
		this.breadcrumbService.setDetail(
			this.translateService.instant("positions.title"),
		);
		this.load();
	}

	ngOnDestroy(): void {
		this.breadcrumbService.clear();
		this.destroy$.next();
		this.destroy$.complete();
	}

	private load(): void {
		this.isLoading = true;
		this.contactService
			.getPuestos()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (puestos) => {
					this.puestos = [...(puestos || [])].sort((a, b) =>
						(a.codigo || "").localeCompare(b.codigo || ""),
					);
					this.isLoading = false;
				},
				error: () => {
					this.isLoading = false;
					this.notificationService.showError(
						this.translateService.instant("positions.loadError"),
					);
				},
			});
		this.contactService
			.getFunciones()
			.pipe(takeUntil(this.destroy$))
			.subscribe((funciones) => {
				this.funciones = funciones || [];
				if (
					this.selectedFuncion &&
					!this.funciones.includes(this.selectedFuncion)
				) {
					this.selectedFuncion = null;
				}
			});
	}

	// --- Derivados para la plantilla ---

	get generales(): PuestoDto[] {
		return this.puestos.filter((p) => p.esGeneral);
	}

	get particulares(): PuestoDto[] {
		if (!this.selectedFuncion) {
			return [];
		}
		const funcion = this.selectedFuncion.trim().toLowerCase();
		return this.puestos.filter(
			(p) =>
				!p.esGeneral &&
				(p.funcionAgregada || "").trim().toLowerCase() === funcion,
		);
	}

	/** Vista previa: lo que verá una persona de la función seleccionada (solo activos). */
	get disponiblesPreview(): PuestoDto[] {
		return [...this.generales, ...this.particulares].filter(
			(p) => p.activo !== false,
		);
	}

	// --- Alta / edición en diálogo (espejo del diálogo de Persona) ---

	openCreate(): void {
		this.openDialog({
			mode: this.activeTab === 0 ? "general" : "funcion",
			puesto: null,
			funciones: this.funciones,
			funcionDefault: this.selectedFuncion,
		});
	}

	openEdit(p: PuestoDto): void {
		this.openDialog({
			mode: p.esGeneral ? "general" : "funcion",
			puesto: p,
			funciones: this.funciones,
		});
	}

	private openDialog(data: PositionDialogData): void {
		this.dialog
			.open<PositionDialogComponent, PositionDialogData, boolean>(
				PositionDialogComponent,
				{
					data,
					panelClass: "ds-dialog-panel",
					backdropClass: "ds-dialog-backdrop",
					width: "640px",
					maxWidth: "96vw",
					autoFocus: false,
					restoreFocus: true,
				},
			)
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe((saved) => {
				if (saved) {
					this.load();
				}
			});
	}

	// --- Acciones de fila ---

	/** Activar/retirar sin pasar por el diálogo (acción rápida de la fila). */
	toggleActivo(p: PuestoDto): void {
		this.contactService
			.updatePuesto({ ...p, activo: p.activo === false })
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => this.load(),
				error: () =>
					this.notificationService.showError(
						this.translateService.instant("positions.saveError"),
					),
			});
	}

	/** Borrado con gate de uso: en uso → ofrecer desactivar (el back también lo bloquea). */
	delete(p: PuestoDto): void {
		this.contactService
			.getPuestoEnUso(p.id)
			.pipe(takeUntil(this.destroy$))
			.subscribe((enUso) => {
				if (enUso > 0) {
					this.dialogService
						.confirm({
							type: "confirm",
							icon: "toggle_off",
							title: this.translateService.instant("positions.deleteTitle"),
							subtitle: `${p.codigo} — ${p.description}`,
							message: this.translateService.instant("positions.enUso", {
								count: enUso,
							}),
							confirmText: this.translateService.instant(
								"positions.deactivate",
							),
						})
						.pipe(takeUntil(this.destroy$))
						.subscribe((confirmed) => {
							if (confirmed && p.activo !== false) {
								this.toggleActivo(p);
							}
						});
					return;
				}
				this.dialogService
					.confirm({
						type: "danger",
						title: this.translateService.instant("positions.deleteTitle"),
						subtitle: `${p.codigo} — ${p.description}`,
						message: this.translateService.instant(
							"positions.confirmDelete",
							{ name: p.codigo },
						),
						confirmText: this.translateService.instant("common.delete"),
						confirmTone: "danger",
					})
					.pipe(takeUntil(this.destroy$))
					.subscribe((confirmed) => {
						if (!confirmed) {
							return;
						}
						this.contactService
							.deletePuesto(p.id)
							.pipe(takeUntil(this.destroy$))
							.subscribe({
								next: () => {
									this.notificationService.showSuccess(
										this.translateService.instant("positions.deleted"),
										null,
									);
									this.load();
								},
								error: (err) =>
									this.notificationService.showError(
										err?.error?.detail ||
											this.translateService.instant(
												"positions.deleteError",
											),
									),
							});
					});
			});
	}
}
